import React, { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { api, formatApiErrorDetail } from "../lib/api";

export const FloatingEnquiry = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, subject: "Quick Enquiry" });
      toast.success("Thank you! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="floating-enquiry-btn"
        aria-label="Send an enquiry"
        className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold pl-4 pr-5 py-3.5 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105"
      >
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">Enquiry</span>
      </button>

      {/* Slide-up panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-7">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="enquiry-overlay"
          />
          <div
            className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl fade-up overflow-hidden"
            data-testid="enquiry-panel"
          >
            <div className="bg-[#0F2942] px-6 py-5 text-white flex items-start justify-between">
              <div>
                <h3 className="font-display text-xl font-bold flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-amber-400" /> Send an Enquiry
                </h3>
                <p className="text-sm text-slate-300 mt-1">We usually respond within 24 hours.</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="enquiry-close-btn" aria-label="Close"
                className="p-1.5 rounded-lg hover:bg-white/10">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <Label htmlFor="enq-name">Full Name *</Label>
                <Input id="enq-name" required value={form.name} onChange={set("name")} placeholder="Your name"
                  data-testid="enquiry-name-input" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="enq-email">Email *</Label>
                  <Input id="enq-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com"
                    data-testid="enquiry-email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="enq-phone">Phone</Label>
                  <Input id="enq-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..."
                    data-testid="enquiry-phone-input" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="enq-msg">Message *</Label>
                <Textarea id="enq-msg" required rows={3} value={form.message} onChange={set("message")}
                  placeholder="How can we help you?" data-testid="enquiry-message-input" className="mt-1.5" />
              </div>
              <button type="submit" disabled={loading} data-testid="enquiry-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold py-3 rounded-full transition-all">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {loading ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingEnquiry;
